import { uploadToCloudinary } from './cloudinary';

export const DEFAULT_COMPRESS_THRESHOLD_BYTES = 500 * 1024;

/** Cloudinary unsigned upload limit (align with plan / API errors). */
export const MAX_CLOUDINARY_UPLOAD_BYTES = 10 * 1024 * 1024;

export const formatFileSize = (bytes) => {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / 1024).toFixed(1)} KB`;
};

export const compressImage = (file, { maxSide = 1280, quality = 0.8 } = {}) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(objectUrl);

            let { width, height } = img;
            if (width > maxSide || height > maxSide) {
                if (width >= height) {
                    height = Math.round((height * maxSide) / width);
                    width = maxSide;
                } else {
                    width = Math.round((width * maxSide) / height);
                    height = maxSide;
                }
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            canvas.toBlob(
                (blob) => (blob ? resolve(blob) : reject(new Error('Canvas compression failed'))),
                'image/jpeg',
                quality,
            );
        };

        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Failed to load image'));
        };

        img.src = objectUrl;
    });

export const uploadImageWithOptionalCompression = async (
    file,
    {
        thresholdBytes = DEFAULT_COMPRESS_THRESHOLD_BYTES,
        promptCompress,
        compressionOptions,
    } = {},
) => {
    let fileToUpload = file;

    if (file.size > thresholdBytes) {
        const shouldCompress = promptCompress ? await promptCompress(file) : true;
        if (shouldCompress) {
            fileToUpload = await compressImage(file, compressionOptions);
        }
    }

    if (fileToUpload.size > MAX_CLOUDINARY_UPLOAD_BYTES) {
        throw new Error(
            'The image is still over 10 MB and cannot be uploaded. Try a smaller or simpler image, or re-export at lower quality.',
        );
    }

    return uploadToCloudinary(fileToUpload);
};

export const dataUrlToFile = async (dataUrl, filename) => {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const mime = blob.type || 'image/jpeg';
    return new File([blob], filename, { type: mime });
};

export const extensionFromDataUrl = (dataUrl) => {
    const mimeMatch = dataUrl.match(/^data:([^;,]+)/);
    const mime = (mimeMatch && mimeMatch[1]) || '';
    if (mime.includes('png')) return 'png';
    if (mime.includes('webp')) return 'webp';
    if (mime.includes('gif')) return 'gif';
    if (mime.includes('svg')) return 'svg';
    return 'jpg';
};

/**
 * Replaces <img src="data:..."> with Cloudinary URLs (same upload + size threshold as manual flow).
 * No compression prompt — large embedded images are compressed automatically before upload.
 *
 * @param {Document} dom - Parsed HTML document (e.g. from DOMParser)
 * @returns {Promise<number>} Count of images successfully uploaded
 */
export const rewriteDataUrlImagesInDocument = async (dom) => {
    const images = dom.querySelectorAll('img[src^="data:"]');
    let uploaded = 0;
    let i = 0;
    for (const img of images) {
        const src = img.getAttribute('src');
        if (!src || !src.startsWith('data:')) continue;
        try {
            const ext = extensionFromDataUrl(src);
            const file = await dataUrlToFile(src, `import-image-${i}.${ext}`);
            const url = await uploadImageWithOptionalCompression(file, {
                thresholdBytes: DEFAULT_COMPRESS_THRESHOLD_BYTES,
                // omit promptCompress → large files compress without modal
            });
            img.setAttribute('src', url);
            uploaded += 1;
            i += 1;
        } catch (e) {
            console.error('Failed to upload imported embedded image', e);
        }
    }
    return uploaded;
};
